// Runtime: le attività vive come filiere che si accendono. Per ogni sessione una corsia:
// la richiesta, il PM, i lavori in ordine (agente, stato, l'ultima cosa che fa) e l'esito.
// Tutto dal registro e dallo stream: lavori del piano e traccia.
import { useEffect } from 'react';
import { lastSaid } from '../../lib/crew';
import { Link } from 'react-router-dom';
import type { MissionTask, Session, TraceEntry } from '../../types';
import { api } from '../../lib/api';
import { EmptyState } from '../../components/EmptyState';
import { Icon } from '../../components/Icon';
import { Pop } from '../../components/Pop';
import { useFace } from '../../lib/useFaces';
import { PageHeader } from '../../components/PageHeader';
import { StatusPill } from '../../components/StatusPill';
import { useStore } from '../../store/StoreProvider';
import { openSessions } from '../../store/selectors';

const TONE: Record<MissionTask['state'], string> = {
  queued: 'var(--border-strong)',
  waiting_gpu: 'var(--warn)',
  running: 'var(--info)',
  done: 'var(--ok)',
  failed: 'var(--danger)',
  cancelled: 'var(--border-strong)',
};
const TINT_OF: Record<string, string> = {
  'var(--accent)': 'lilac',
  'var(--info)': 'blue',
  'var(--ok)': 'mint',
  'var(--warn)': 'sun',
  'var(--danger)': 'coral',
  'var(--border-strong)': 'lilac',
};
const LABEL: Record<MissionTask['state'], string> = {
  queued: 'in coda',
  waiting_gpu: 'aspetta il modello',
  running: 'al lavoro',
  done: 'fatto',
  failed: 'fermato',
  cancelled: 'annullato',
};

export function ActivityRuntime() {
  const { state, dispatch } = useStore();
  const live = openSessions(state);
  const ids = live.map((s) => s.id).join(',');
  // le sessioni per progetto: più corsie nello stesso progetto sono lavori che si incrociano
  const byProject = new Map<string, Session[]>();
  for (const s of live) {
    const key = s.projectName ?? 'Senza progetto';
    byProject.set(key, [...(byProject.get(key) ?? []), s]);
  }
  const groups = [...byProject];

  // i piani delle attività vive; poi li tiene aggiornati lo stream
  useEffect(() => {
    for (const id of ids.split(',').filter(Boolean))
      void api.tasks(id).then((tasks) => dispatch({ type: 'tasks/set', sessionId: id, tasks }));
  }, [ids, dispatch]);

  return (
    <div className="page">
      <PageHeader
        title="Runtime"
        subtitle={`${live.length} attività in corso: chi sta lavorando, su cosa, a che punto.`}
      />
      {live.length === 0 ? (
        <EmptyState
          icon="git-branch"
          pop="devops-01"
          title="Nessuna attività in corso"
          hint="Quando dai un compito a PopTown, qui si accende la sua filiera."
        />
      ) : (
        <div style={{ display: 'grid', gap: 20 }}>
          {groups.map(([name, sessions]) => (
            <section key={name} style={{ display: 'grid', gap: 10 }} aria-label={name}>
              <h2 className="t-h2" style={{ margin: 0 }}>
                {name} · {sessions.length} {sessions.length === 1 ? 'sessione' : 'sessioni insieme'}
              </h2>
              {sessions.map((s) => (
                <Lane key={s.id} session={s} tasks={state.tasks[s.id] ?? []} trace={state.trace} />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export function Lane({
  session: s,
  tasks,
  trace,
}: {
  session: Session;
  tasks: MissionTask[];
  trace: TraceEntry[];
}) {
  const mine = trace.filter((t) => t.sessionId === s.id);
  // l'ultima cosa detta dal pop, mai un comando o il suo output (2026-11-02: «shell:» sotto Dario)
  const lastOf = (agent: string) => {
    const said = lastSaid(mine, agent);
    return said ? { text: said } : undefined;
  };
  const pmBusy = s.state === 'planning';
  return (
    <section className="run-lane" aria-label={s.title}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Link to={`/s/${s.id}`} className="run-lane-title">
          {s.title}
        </Link>
        <StatusPill state={s.state} />
        <Link to={`/s/${s.id}`} className="dash-more">
          Apri la sessione
        </Link>
      </div>
      <div className="run-track">
        <Node title="Richiesta" who="tu" tone="var(--accent)" note={s.requestText} />
        <Arrow />
        <Node
          title="Piano"
          who="PM"
          tone={pmBusy ? 'var(--info)' : tasks.length ? 'var(--ok)' : 'var(--border-strong)'}
          busy={pmBusy}
          note={pmBusy ? (lastOf('PM')?.text ?? 'prepara il piano') : `${tasks.length} lavori`}
        />
        {tasks.map((t) => (
          <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
            <Arrow review={Boolean(t.reviews)} />
            <Node
              title={t.title}
              who={t.agent}
              avatar={t.avatar ?? undefined}
              tone={TONE[t.state]}
              busy={t.state === 'running'}
              status={LABEL[t.state]}
              note={t.state === 'running' ? lastOf(t.agent)?.text : (t.error ?? undefined)}
              reviews={t.reviews ? tasks.find((x) => x.id === t.reviews)?.title : undefined}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function Arrow({ review }: { review?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        alignSelf: 'center',
        color: review ? 'var(--warn)' : 'var(--faint)',
        display: 'grid',
        flex: 'none',
      }}
    >
      <Icon name={review ? 'search' : 'chevron-right'} size={16} />
    </span>
  );
}

function Node({
  title,
  who,
  tone,
  busy,
  status,
  note,
  reviews,
  avatar,
}: {
  title: string;
  who: string;
  tone: string;
  busy?: boolean;
  status?: string;
  note?: string;
  reviews?: string;
  avatar?: string;
}) {
  const known = useFace(undefined, who === 'tu' ? undefined : who);
  const face = avatar ?? known;
  // il colore della tappa dalla tinta del suo stato (tela «PopTown Pop Style», Runtime)
  const tint = TINT_OF[tone] ?? 'lilac';
  return (
    <div
      className={busy ? 'run-node anim-pulse-slow' : 'run-node'}
      data-tint={tint}
      data-busy={busy || undefined}
      data-idle={tone === 'var(--border-strong)' || undefined}
    >
      {face ? (
        <Pop avatar={face} size={84} label={who} />
      ) : (
        <span className="run-node-you" aria-hidden>
          <Icon name="user" size={34} />
        </span>
      )}
      <span className="run-node-who">{who}</span>
      <span className="run-node-title clamp2">{title}</span>
      {status && <span className="run-node-status">{status}</span>}
      {reviews && (
        <span className="t-caption clamp2" style={{ maxWidth: '100%' }}>
          controlla «{reviews}»
        </span>
      )}
      {note && (
        <span className="t-caption faint clamp2" title={note} style={{ maxWidth: '100%' }}>
          {note}
        </span>
      )}
    </div>
  );
}
